import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { Link } from 'react-router-dom';
import {
  HelpCircle, ShoppingBag, Truck, ShieldCheck, RefreshCcw, CreditCard, MessageSquareWarning, Store, ChevronDown,
} from 'lucide-react';

const FAQS = [
  {
    id: 'compras', icon: ShoppingBag, title: '¿Cómo comprar en AutoMarket?',
    body: 'Busca el repuesto por marca/modelo/año o categoría, agrégalo al carrito y completa el pago. Puedes calificar al vendedor al recibir tu producto para ayudar a otros compradores.',
  },
  {
    id: 'envios', icon: Truck, title: 'Envíos y tiempos de entrega',
    body: 'El tiempo y el costo de entrega dependen del vendedor, del producto, del origen, del destino y del operador logístico. Cada publicación muestra el tiempo estimado y la cobertura informados por el vendedor, y el comprador los ve antes de pagar. AutoMarket facilita el seguimiento del pedido, pero no fija tiempos generales aplicables a todos los productos ni garantiza directamente el servicio de la transportadora.',
  },
  {
    id: 'pagos', icon: CreditCard, title: 'Medios de pago',
    body: 'Aceptamos todos los métodos de pago habilitados en Colombia: tarjetas de crédito y débito, PSE, transferencias, Nequi, Daviplata y efectivo en corresponsales bancarios.',
  },
  {
    id: 'garantia', icon: ShieldCheck, title: 'Garantía de productos',
    body: 'En cada producto aplica la garantía legal correspondiente (Estatuto del Consumidor – Ley 1480 de 2011) y, cuando exista, la garantía adicional ofrecida por el vendedor y/o fabricante, con el término y las condiciones informados en la publicación. AutoMarket gestiona, hace seguimiento y acompaña el proceso; las obligaciones propias de la garantía corresponden al vendedor y/o fabricante.',
  },
  {
    id: 'devoluciones', icon: RefreshCcw, title: 'Devoluciones',
    body: 'Las devoluciones se atienden conforme a la ley aplicable y a las condiciones informadas por el vendedor en cada producto. Si tu repuesto llega defectuoso, no corresponde con lo anunciado o aplica un derecho legal de retracto, radica una PQR con el número del pedido y una descripción clara del caso; nuestro equipo gestiona y acompaña el proceso con el vendedor.',
  },
  {
    id: 'vender', icon: Store, title: '¿Cómo vender en AutoMarket?',
    body: 'Regístrate como vendedor, completa la información del negocio, escoge tu plan (Básico o PRO) y comienza a publicar productos. Cobramos una comisión sobre cada venta exitosa.',
  },
];

export default function Ayuda() {
  const [open, setOpen] = useState(FAQS[0].id);

  useSEO({
    title: 'Centro de ayuda | AutoMarket Colombia',
    description: 'Resuelve tus dudas sobre compras, envíos, garantías, pagos y devoluciones en AutoMarket Colombia.',
    canonical: '/ayuda',
  });

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Soporte</div>
        <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-3">
          <HelpCircle className="h-8 w-8 text-slate-600" /> Centro de ayuda
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Encuentra respuestas rápidas a las preguntas más frecuentes. Si no encuentras lo que buscas,
          radica una PQR y te responderemos en menos de 48 horas hábiles.
        </p>

        <div className="mt-8 border border-slate-200 bg-white divide-y divide-slate-200" data-testid="faq-list">
          {FAQS.map((f) => {
            const active = open === f.id;
            const Icon = f.icon;
            return (
              <div key={f.id} id={f.id} data-testid={`faq-${f.id}`}>
                <button
                  onClick={() => setOpen(active ? null : f.id)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50"
                >
                  <div className={`flex h-9 w-9 items-center justify-center transition-colors ${active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <span className="flex-1 font-display font-bold text-slate-900">{f.title}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${active ? 'rotate-180' : ''}`} />
                </button>
                {active && (
                  <div className="px-16 pb-5 text-sm text-slate-700 leading-relaxed">
                    {f.body}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 border border-slate-200 bg-slate-50 p-6 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="font-display font-black text-slate-900 text-lg">¿No encontraste tu respuesta?</div>
            <div className="text-sm text-slate-600 mt-1">Radica una petición, queja, reclamo o sugerencia.</div>
          </div>
          <Link
            to="/pqr"
            data-testid="ayuda-pqr-cta"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-5 h-11 transition-colors"
          >
            <MessageSquareWarning className="h-4 w-4" /> Ir a PQR
          </Link>
        </div>
      </div>
    </Layout>
  );
}
